/**
 * InkQuote API Integration Service
 * Handles communication with the InkQuote API for management features
 * Only active in development mode
 */

class InkQuoteApiService {
  constructor() {
    // ALWAYS use the local dev server proxy to avoid CORS issues
    // The dev server will proxy requests to the actual InkQuote API
    this.apiBaseUrl = 'http://localhost:3001/api';
    this.apiKey = process.env.REACT_APP_INKQUOTE_API_KEY || '';
    this.cache = new Map();
    this.cacheTimeouts = new Map();
    this.isDevMode = process.env.REACT_APP_MODE === 'development';
    this.offlineQueue = [];
    this.isOnline = true;
  }

  /**
   * Check if dev mode is enabled
   */
  isEnabled() {
    return this.isDevMode;
  }

  /**
   * Check API connection status
   */
  async checkConnection() {
    if (!this.isEnabled()) {
      return { connected: false, reason: 'Dev mode not enabled' };
    }

    try {
      // Create abort controller for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      const response = await fetch(`${this.apiBaseUrl}/connection-status`, {
        method: 'GET',
        headers: this._getHeaders(),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        this.isOnline = true;
        return { connected: true };
      } else {
        this.isOnline = false;
        return { connected: false, reason: `HTTP ${response.status}` };
      }
    } catch (error) {
      this.isOnline = false;
      return { connected: false, reason: error.message };
    }
  }

  /**
   * Get request headers with API key
   */
  _getHeaders() {
    const headers = {
      'Content-Type': 'application/json'
    };

    if (this.apiKey) {
      headers['X-API-Key'] = this.apiKey;
    }

    return headers;
  }

  /**
   * Handle API errors and convert to user-friendly messages
   */
  _handleError(error, operation) {
    console.error(`[InkQuoteAPI] ${operation} failed:`, error);

    const errorMap = {
      'Failed to fetch': 'Cannot connect to API. Check if the server is running.',
      'NetworkError': 'Network error. Check your internet connection.',
      'Unauthorized': 'Invalid API key. Check your environment variables.',
      'Not Found': 'Resource not found on the API.',
      'Timeout': 'Request timed out. The API might be slow or unavailable.'
    };

    const message = errorMap[error.message] || error.message || 'Unknown error occurred';

    return {
      success: false,
      error: message,
      offline: !this.isOnline
    };
  }

  /**
   * Update skip categories for a quote
   * @param {string} quoteId - Quote ID
   * @param {Array<string>} skipCategories - Array of skip category strings
   */
  async updateSkipCategories(quoteId, skipCategories) {
    if (!this.isEnabled()) {
      console.warn('[InkQuoteAPI] Skip categories update attempted outside dev mode');
      return { success: false, error: 'Dev mode not enabled' };
    }

    try {
      const response = await fetch(`${this.apiBaseUrl}/skip-categories/${quoteId}`, {
        method: 'POST',
        headers: this._getHeaders(),
        body: JSON.stringify({ skipCategories })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      this.isOnline = true;

      // Process any queued operations after successful connection
      if (this.offlineQueue.length > 0) {
        this._processOfflineQueue();
      }

      return {
        success: true,
        data
      };
    } catch (error) {
      // Queue for later if offline
      if (!navigator.onLine || error.message.includes('fetch')) {
        this.isOnline = false;
        this.offlineQueue.push({
          type: 'skipCategories',
          quoteId,
          skipCategories,
          timestamp: Date.now()
        });

        return {
          success: false,
          queued: true,
          error: 'Offline - change queued for sync'
        };
      }

      return this._handleError(error, 'updateSkipCategories');
    }
  }

  /**
   * Update quote text content
   * @param {string} quoteId - Quote ID
   * @param {string} content - New content
   * @param {string} author - Author name (optional)
   */
  async updateQuoteContent(quoteId, content, author) {
    if (!this.isEnabled()) {
      console.warn('[InkQuoteAPI] Quote content update attempted outside dev mode');
      return { success: false, error: 'Dev mode not enabled' };
    }

    try {
      const payload = { content };
      if (author !== undefined) {
        payload.author = author;
      }

      const response = await fetch(`${this.apiBaseUrl}/quotes/${quoteId}`, {
        method: 'PUT',
        headers: this._getHeaders(),
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      this.isOnline = true;

      return {
        success: true,
        data
      };
    } catch (error) {
      if (!navigator.onLine || error.message.includes('fetch')) {
        this.isOnline = false;
        this.offlineQueue.push({
          type: 'quoteContent',
          quoteId,
          content,
          author,
          timestamp: Date.now()
        });

        return {
          success: false,
          queued: true,
          error: 'Offline - change queued for sync'
        };
      }

      return this._handleError(error, 'updateQuoteContent');
    }
  }

  /**
   * Trigger export and download of updated JSON files
   */
  async triggerExport() {
    if (!this.isEnabled()) {
      console.warn('[InkQuoteAPI] Export triggered outside dev mode');
      return { success: false, error: 'Dev mode not enabled' };
    }

    try {
      const response = await fetch(`${this.apiBaseUrl}/trigger-export`, {
        method: 'POST',
        headers: this._getHeaders()
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      this.isOnline = true;

      return {
        success: true,
        data
      };
    } catch (error) {
      return this._handleError(error, 'triggerExport');
    }
  }

  /**
   * Process queued offline operations
   */
  async _processOfflineQueue() {
    if (this.offlineQueue.length === 0 || !this.isOnline) {
      return;
    }

    if (process.env.REACT_APP_MODE === 'development') {
      console.log(`[InkQuoteAPI] Processing ${this.offlineQueue.length} queued operations...`);
    }

    const queue = [...this.offlineQueue];
    this.offlineQueue = [];

    for (const operation of queue) {
      try {
        if (operation.type === 'skipCategories') {
          await this.updateSkipCategories(operation.quoteId, operation.skipCategories);
        } else if (operation.type === 'quoteContent') {
          await this.updateQuoteContent(operation.quoteId, operation.content, operation.author);
        }
      } catch (error) {
        console.error('[InkQuoteAPI] Failed to process queued operation:', error);
        // Re-queue if it fails
        this.offlineQueue.push(operation);
      }
    }

    if (this.offlineQueue.length === 0 && process.env.REACT_APP_MODE === 'development') {
      console.log('[InkQuoteAPI] All queued operations processed successfully');
    }
  }

  /**
   * Get number of queued operations
   */
  getQueuedCount() {
    return this.offlineQueue.length;
  }

  /**
   * Clear offline queue (use with caution)
   */
  clearQueue() {
    const count = this.offlineQueue.length;
    this.offlineQueue = [];
    if (process.env.REACT_APP_MODE === 'development') {
      console.log(`[InkQuoteAPI] Cleared ${count} queued operations`);
    }
    return count;
  }

  /**
   * Manual sync trigger
   */
  async syncNow() {
    if (!this.isEnabled()) {
      return { success: false, error: 'Dev mode not enabled' };
    }

    const connectionStatus = await this.checkConnection();

    if (connectionStatus.connected) {
      await this._processOfflineQueue();
      return { success: true, synced: true };
    }

    return { success: false, error: connectionStatus.reason };
  }
}

// Export singleton instance (for dev mode with proxy)
const inkquoteApi = new InkQuoteApiService();
export default inkquoteApi;

// Client-side direct API access (always available, no dev mode required)
// Uses localStorage for user-configured API endpoint
const STORAGE_KEYS = {
  BASE_URL: 'inkquote_base_url',
  API_KEY: 'inkquote_api_key',
  ENABLED: 'inkquote_enabled'
};

export const InkQuoteApi = {
  // Configuration Management
  getConfig() {
    return {
      baseUrl: localStorage.getItem(STORAGE_KEYS.BASE_URL) || '',
      apiKey: localStorage.getItem(STORAGE_KEYS.API_KEY) || '',
      enabled: localStorage.getItem(STORAGE_KEYS.ENABLED) === 'true'
    };
  },

  saveConfig(baseUrl, apiKey) {
    localStorage.setItem(STORAGE_KEYS.BASE_URL, baseUrl);
    localStorage.setItem(STORAGE_KEYS.API_KEY, apiKey);
    localStorage.setItem(STORAGE_KEYS.ENABLED, 'true');
  },

  isConfigured() {
    const config = this.getConfig();
    return !!(config.baseUrl && config.apiKey);
  },

  // Direct API Calls (client-side, no proxy)
  async fetchRecentQuotes(limit = 100) {
    const { baseUrl, apiKey } = this.getConfig();
    if (!baseUrl || !apiKey) {
      throw new Error('InkQuote API not configured');
    }

    const response = await fetch(`${baseUrl}/inkquote/history/recent?limit=${limit}`, {
      headers: { 'X-API-Key': apiKey }
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch quotes: ${response.statusText}`);
    }

    return response.json();
  },

  async updateQuote(quoteId, updates) {
    const { baseUrl, apiKey } = this.getConfig();
    if (!baseUrl || !apiKey) {
      throw new Error('InkQuote API not configured');
    }

    const response = await fetch(`${baseUrl}/inkquote/quotes/${quoteId}`, {
      method: 'PUT',
      headers: {
        'X-API-Key': apiKey,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(updates)
    });

    if (!response.ok) {
      throw new Error(`Failed to update quote: ${response.statusText}`);
    }

    return response.json();
  },

  async skipQuote(quoteId, categories = []) {
    return this.updateQuote(quoteId, {
      skip: true,
      skip_categories: categories
    });
  },

  async deleteQuote(quoteId) {
    const { baseUrl, apiKey } = this.getConfig();
    if (!baseUrl || !apiKey) {
      throw new Error('InkQuote API not configured');
    }

    const response = await fetch(`${baseUrl}/inkquote/quotes/${quoteId}`, {
      method: 'DELETE',
      headers: {
        'X-API-Key': apiKey
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to delete quote: ${response.statusText}`);
    }

    return response.json();
  },

  // Tag Management Methods
  async getAllTags() {
    const { baseUrl, apiKey } = this.getConfig();
    if (!baseUrl || !apiKey) {
      throw new Error('InkQuote API not configured');
    }

    const response = await fetch(`${baseUrl}/inkquote/quotes`, {
      headers: { 'X-API-Key': apiKey }
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch quotes: ${response.statusText}`);
    }

    const data = await response.json();
    const quotes = data.quotes || data;

    // Extract all unique tags from all quotes
    const tagSet = new Set();
    quotes.forEach(quote => {
      const metadata = quote.metadata || {};
      const tags = metadata.tags || [];
      tags.forEach(tag => tagSet.add(tag));
    });

    return Array.from(tagSet).sort();
  },

  async getQuoteTags(quoteId) {
    const { baseUrl, apiKey } = this.getConfig();
    if (!baseUrl || !apiKey) {
      throw new Error('InkQuote API not configured');
    }

    const response = await fetch(`${baseUrl}/inkquote/quotes/${quoteId}`, {
      headers: { 'X-API-Key': apiKey }
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch quote: ${response.statusText}`);
    }

    const quote = await response.json();
    return quote.metadata?.tags || [];
  },

  async updateQuoteTags(quoteId, tags) {
    const { baseUrl, apiKey } = this.getConfig();
    if (!baseUrl || !apiKey) {
      throw new Error('InkQuote API not configured');
    }

    // First get the current quote to preserve other metadata
    const currentQuote = await fetch(`${baseUrl}/inkquote/quotes/${quoteId}`, {
      headers: { 'X-API-Key': apiKey }
    }).then(r => r.json());

    const updatedMetadata = {
      ...(currentQuote.metadata || {}),
      tags: tags
    };

    const response = await fetch(`${baseUrl}/inkquote/quotes/${quoteId}`, {
      method: 'PUT',
      headers: {
        'X-API-Key': apiKey,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ metadata: updatedMetadata })
    });

    if (!response.ok) {
      throw new Error(`Failed to update tags: ${response.statusText}`);
    }

    return response.json();
  },

  // Favorite Management Methods
  async toggleFavorite(quoteId, isFavorite) {
    const { baseUrl, apiKey } = this.getConfig();
    if (!baseUrl || !apiKey) {
      throw new Error('InkQuote API not configured');
    }

    // First try to get the current quote to check if it exists in the API
    const quoteResponse = await fetch(`${baseUrl}/inkquote/quotes/${quoteId}`, {
      headers: { 'X-API-Key': apiKey }
    });

    // If quote doesn't exist in API (404), this is a local-only item (bookmark, reddit, etc.)
    // Return success but don't try to update - localStorage will handle it
    if (!quoteResponse.ok) {
      if (quoteResponse.status === 404) {
        return {
          success: true,
          localOnly: true,
          message: 'Quote not in API, updated locally only'
        };
      }
      throw new Error(`Failed to fetch quote: ${quoteResponse.statusText}`);
    }

    const currentQuote = await quoteResponse.json();

    // Get current tags from metadata
    const currentTags = currentQuote.metadata?.tags || [];

    // Add or remove 'favorite' tag
    let updatedTags;
    if (isFavorite) {
      // Add 'favorite' tag if not already present
      updatedTags = currentTags.includes('favorite')
        ? currentTags
        : [...currentTags, 'favorite'];
    } else {
      // Remove 'favorite' tag
      updatedTags = currentTags.filter(tag => tag !== 'favorite');
    }

    // Update the quote with modified tags in metadata
    const updatedMetadata = {
      ...(currentQuote.metadata || {}),
      tags: updatedTags
    };

    const response = await fetch(`${baseUrl}/inkquote/quotes/${quoteId}`, {
      method: 'PUT',
      headers: {
        'X-API-Key': apiKey,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ metadata: updatedMetadata })
    });

    if (!response.ok) {
      throw new Error(`Failed to update favorite: ${response.statusText}`);
    }

    return response.json();
  },

  // Skip Categories Management
  async updateSkipCategories(quoteId, skipCategories) {
    const { baseUrl, apiKey } = this.getConfig();
    if (!baseUrl || !apiKey) {
      throw new Error('InkQuote API not configured');
    }

    // Update with proper field names
    const updates = {
      skip: skipCategories.length > 0,
      skip_categories: skipCategories
    };

    const response = await fetch(`${baseUrl}/inkquote/quotes/${quoteId}`, {
      method: 'PUT',
      headers: {
        'X-API-Key': apiKey,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(updates)
    });

    if (!response.ok) {
      throw new Error(`Failed to update skip categories: ${response.statusText}`);
    }

    return response.json();
  },

  // Integration Methods
  async getIntegrations() {
    const { baseUrl, apiKey } = this.getConfig();
    if (!baseUrl || !apiKey) {
      throw new Error('InkQuote API not configured');
    }

    const response = await fetch(`${baseUrl}/inkquote/integrations`, {
      headers: { 'X-API-Key': apiKey }
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch integrations: ${response.statusText}`);
    }

    return response.json();
  },

  async runIntegration(integrationName) {
    const { baseUrl, apiKey } = this.getConfig();
    if (!baseUrl || !apiKey) {
      throw new Error('InkQuote API not configured');
    }

    const response = await fetch(`${baseUrl}/inkquote/integrations/${integrationName}/run`, {
      method: 'POST',
      headers: {
        'X-API-Key': apiKey,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({})
    });

    if (!response.ok) {
      throw new Error(`Failed to run integration: ${response.statusText}`);
    }

    return response.json();
  },

  async uploadTwitterFile(file) {
    const { baseUrl, apiKey } = this.getConfig();
    if (!baseUrl || !apiKey) {
      throw new Error('InkQuote API not configured');
    }

    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${baseUrl}/inkquote/integrations/twitter/import`, {
      method: 'POST',
      headers: {
        'X-API-Key': apiKey
        // Don't set Content-Type - let browser set it with boundary for FormData
      },
      body: formData
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Upload failed: ${response.status} ${errorText}`);
    }

    return response.json();
  }
};
